import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Card,
  Empty,
  Form,
  InputNumber,
  Modal,
  Select,
  Table,
  Typography,
} from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
// Api
import { GET_TICKET_EVENT_AFFILIATES } from 'api/ticketing/queries';
// Types
import {
  GetTicketEventAffiliates,
  GetTicketEventAffiliatesVariables,
  GetTicketEventAffiliates_getStreamAffiliateLinks_entities,
} from 'api/ticketing/types/GetTicketEventAffiliates';
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
import {
  StreamAffiliateLinksOrderBy,
  StreamAffiliateLinkStatus,
} from 'api/graphql-global-types';
// Hooks
import { useServerTable } from 'pages/Events/hooks/useServerTable';
import {
  AffiliateOwner,
  NewAffiliateValues,
  useAffiliateActions,
} from './hooks/useAffiliateActions';
// Helpers
import {
  AFFILIATE_STATUS_META,
  formatCount,
  formatMoney,
  getCommissionRules,
  getPersonName,
  pluralize,
  validateForm,
} from 'pages/Events/helpers';
// Constants
import { EDIT_TICKET_EVENT_GEN } from 'constants/routes';
// Components
import LoadErrorAlert from 'pages/Events/components/LoadErrorAlert/LoadErrorAlert';
import StatusTag from 'pages/Events/components/StatusTag/StatusTag';
import UserSelect from '../UserSelect/UserSelect';
import AffiliateRowActions from './components/AffiliateRowActions/AffiliateRowActions';
// Styles
import styles from './AffiliatesCard.module.scss';

const { Text } = Typography;

type AffiliateRow = GetTicketEventAffiliates_getStreamAffiliateLinks_entities;

const PAGE_SIZE = 10;

const SORT_COLUMNS: Record<string, StreamAffiliateLinksOrderBy> = {
  name: StreamAffiliateLinksOrderBy.Name,
  clicks: StreamAffiliateLinksOrderBy.Clicks,
  ticketsSold: StreamAffiliateLinksOrderBy.TicketsSold,
  revenue: StreamAffiliateLinksOrderBy.Revenue,
  commission: StreamAffiliateLinksOrderBy.Commission,
};

type AffiliatesCardProps = {
  event: TicketEventDetailsFields;
  owner: AffiliateOwner;
  onChanged: () => void;
};

const AffiliatesCard = ({
  event,
  owner,
  onChanged,
}: AffiliatesCardProps): JSX.Element => {
  const history = useHistory();
  const [addForm] = Form.useForm<NewAffiliateValues>();
  const [rateForm] = Form.useForm<{ commissionPercent: number }>();
  const [status, setStatus] = useState<StreamAffiliateLinkStatus | null>(null);
  const table = useServerTable({
    sortColumns: SORT_COLUMNS,
    defaultSortBy: StreamAffiliateLinksOrderBy.Clicks,
    defaultPageSize: PAGE_SIZE,
  });
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<AffiliateRow | null>(null);
  const programOff = !event.streamId && !event.affiliateEnabled;

  const { data, loading, error, refetch } = useQuery<
    GetTicketEventAffiliates,
    GetTicketEventAffiliatesVariables
  >(GET_TICKET_EVENT_AFFILIATES, {
    variables: {
      input: {
        ...owner,
        status,
        orderBy: table.sortBy,
        direction: table.direction,
        offset: table.offset,
        limit: table.pageSize,
      },
    },
    skip: programOff,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const afterChange = (): void => {
    refetch();
    onChanged();
  };
  const {
    addAffiliate,
    setCommission,
    regenerateLink,
    toggleLinkStatus,
    adding,
    savingRate,
  } = useAffiliateActions(owner, afterChange);

  const handleAdd = async (): Promise<void> => {
    const values = await validateForm(addForm);

    if (values && (await addAffiliate(values))) {
      addForm.resetFields();
      setAddOpen(false);
    }
  };

  const handleEditRate = (row: AffiliateRow): void => {
    rateForm.setFieldsValue({ commissionPercent: row.link.commissionPercent });
    setEditing(row);
  };

  const handleSaveRate = async (): Promise<void> => {
    if (!editing) {
      return;
    }

    const values = await validateForm(rateForm);

    if (
      values &&
      (await setCommission(editing.link.id, values.commissionPercent))
    ) {
      setEditing(null);
    }
  };

  const { sortOrder } = table;

  if (programOff) {
    return (
      <Card title="Affiliates" className={styles.card}>
        <Empty
          description={
            <>
              <Text strong>Affiliate program is off</Text>
              <br />
              <Text type="secondary">
                Turn it on in Edit and every participant gets a unique affiliate
                link. You can also hand links to anyone else on MILLIONS.
              </Text>
            </>
          }
        >
          <Button
            onClick={() => history.push(`${EDIT_TICKET_EVENT_GEN}/${event.id}`)}
          >
            Edit event
          </Button>
        </Empty>
      </Card>
    );
  }

  const rows = data?.getStreamAffiliateLinks.entities || [];

  return (
    <Card
      title="Affiliates"
      className={styles.card}
      extra={
        <div className={styles.toolbar}>
          <Select<StreamAffiliateLinkStatus | ''>
            size="small"
            value={status || ''}
            aria-label="Filter affiliates by status"
            onChange={(value) => {
              setStatus(value || null);
              table.setPage(1);
            }}
            options={[
              { value: '', label: 'All' },
              { value: StreamAffiliateLinkStatus.Active, label: 'Active' },
              { value: StreamAffiliateLinkStatus.Disabled, label: 'Disabled' },
            ]}
            className={styles.statusFilter}
          />
          <Button
            type="primary"
            size="small"
            icon={<UserAddOutlined />}
            onClick={() => setAddOpen(true)}
          >
            Add affiliate
          </Button>
        </div>
      }
    >
      {!event.streamId && (
        <Text type="secondary" className={styles.rate}>
          Event rate: {event.affiliateCommissionPercent}% of each ticket sale.
          Participants get a link automatically.
        </Text>
      )}
      {error ? (
        <LoadErrorAlert
          message="Failed to load the affiliates"
          error={error}
          onRetry={refetch}
        />
      ) : (
        <Table<AffiliateRow>
          rowKey={(row) => row.link.id}
          size="small"
          loading={loading}
          dataSource={rows}
          onChange={table.onChange}
          scroll={{ x: 820 }}
          locale={{
            emptyText: status
              ? 'Nobody matches this status.'
              : 'No affiliates yet. Participants get a link automatically when they join; use Add affiliate for anyone else.',
          }}
          pagination={{
            current: table.page,
            pageSize: table.pageSize,
            total: data?.getStreamAffiliateLinks.total || 0,
            hideOnSinglePage: true,
            showTotal: (count) => pluralize(count, 'affiliate'),
          }}
          columns={[
            {
              title: 'Affiliate',
              key: 'name',
              sorter: true,
              sortOrder: sortOrder('name'),
              render: (_, row) => (
                <div className={styles.stack}>
                  <span>
                    {getPersonName(row.link.user)}
                    {row.isParticipant && (
                      <Text type="secondary"> · participant</Text>
                    )}
                  </span>
                  <Text type="secondary" className={styles.small}>
                    {row.link.user.email}
                  </Text>
                </div>
              ),
            },
            {
              title: 'Clicks',
              key: 'clicks',
              sorter: true,
              sortOrder: sortOrder('clicks'),
              render: (_, row) => formatCount(row.link.clicks),
            },
            {
              title: 'Ticket sales',
              key: 'ticketsSold',
              sorter: true,
              sortOrder: sortOrder('ticketsSold'),
              render: (_, row) => row.ticketsSoldCount,
            },
            {
              title: 'Revenue',
              key: 'revenue',
              sorter: true,
              sortOrder: sortOrder('revenue'),
              render: (_, row) =>
                row.salesCount + row.ticketsSoldCount
                  ? formatMoney(row.revenue)
                  : '—',
            },
            {
              title: 'Commission earned',
              key: 'commission',
              sorter: true,
              sortOrder: sortOrder('commission'),
              render: (_, row) => (
                <div className={styles.stack}>
                  <span>{formatMoney(row.commissionEarned)}</span>
                  <Text type="secondary" className={styles.small}>
                    {row.link.commissionPercent}% rate
                  </Text>
                </div>
              ),
            },
            {
              title: 'Status',
              key: 'status',
              render: (_, row) => (
                <StatusTag meta={AFFILIATE_STATUS_META[row.link.status]} />
              ),
            },
            {
              title: '',
              key: 'actions',
              align: 'right' as const,
              render: (_, row) => (
                <AffiliateRowActions
                  row={row}
                  onEditRate={handleEditRate}
                  onRegenerate={regenerateLink}
                  onToggleStatus={toggleLinkStatus}
                />
              ),
            },
          ]}
        />
      )}
      <Text type="secondary" className={styles.small}>
        Refunded or cancelled orders don&apos;t count toward commission.
      </Text>

      <Modal
        title="Add affiliate"
        visible={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={handleAdd}
        okText="Create link"
        confirmLoading={adding}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary">
          Anyone with a MILLIONS profile can promote this event with their own
          link. They get the event rate unless you set one here.
        </Typography.Paragraph>
        <Form form={addForm} layout="vertical" preserve={false}>
          <Form.Item
            name="userId"
            label="Person"
            rules={[{ required: true, message: 'Choose who gets the link' }]}
          >
            <UserSelect aria-label="Affiliate" />
          </Form.Item>
          <Form.Item
            name="commissionPercent"
            label="Commission (%)"
            extra="Leave empty to use the event rate."
            rules={getCommissionRules(false)}
          >
            <InputNumber
              min={0}
              max={100}
              precision={2}
              placeholder={String(event.affiliateCommissionPercent ?? '')}
              className={styles.fullWidth}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit commission rate"
        visible={!!editing}
        onCancel={() => setEditing(null)}
        onOk={handleSaveRate}
        okText="Save"
        confirmLoading={savingRate}
      >
        <Typography.Paragraph type="secondary">
          {editing ? getPersonName(editing.link.user) : ''} earns this share of
          every ticket order made through their link. Orders already made keep
          their rate.
        </Typography.Paragraph>
        <Form form={rateForm} layout="vertical">
          <Form.Item
            name="commissionPercent"
            label="Commission (%)"
            rules={getCommissionRules(true)}
          >
            <InputNumber
              min={0}
              max={100}
              precision={2}
              className={styles.fullWidth}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default AffiliatesCard;
